import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import * as Sentry from '@sentry/react';
import { AuthProvider, useAuth } from '@/features/auth/contexts/AuthContext';
import { RotateCw } from 'lucide-react';

import MainLayout from '@/layouts/MainLayout';
import PageTransition from '@/components/PageTransition';

// Lazy loaded feature components
const Dashboard = React.lazy(() => import('@/features/dashboard/pages/Dashboard'));
const LLMLogger = React.lazy(() => import('@/features/monitoring/pages/LLMLogger'));
const MailNotifier = React.lazy(() => import('@/features/mail/pages/MailNotifier'));
const InvoicingService = React.lazy(() => import('@/features/billing/pages/InvoicingService'));
const SubscriptionsPage = React.lazy(() => import('@/features/billing/pages/SubscriptionsPage'));
const PaymentApps = React.lazy(() => import('@/features/billing/pages/PaymentApps'));
const WebhookDeadLetter = React.lazy(() => import('@/features/billing/pages/WebhookDeadLetter'));
const CompliancePage = React.lazy(() => import('@/features/billing/pages/CompliancePage'));
const AppsManagement = React.lazy(() => import('@/features/apps/pages/AppsManagement'));
const UserManagement = React.lazy(() => import('@/features/users/pages/UserManagement'));
const ServiceAgentsManagement = React.lazy(() => import('@/features/service-agents/pages/ServiceAgentsManagement'));
const SchemasPage = React.lazy(() => import('@/features/content-management/pages/SchemasPage'));
const SchemaCreatePage = React.lazy(() => import('@/features/content-management/pages/SchemaCreatePage'));
const SchemaDetailPage = React.lazy(() => import('@/features/content-management/pages/SchemaDetailPage'));
const ContentsPage = React.lazy(() => import('@/features/content-management/pages/ContentsPage'));
const ContentCreatePage = React.lazy(() => import('@/features/content-management/pages/ContentCreatePage'));
const ContentEditPage = React.lazy(() => import('@/features/content-management/pages/ContentEditPage'));
const PublicLegalPage = React.lazy(() => import('@/features/legal/pages/PublicLegalPage'));

const APP_SLUG = 'rolling_skeleton';

const hasContentManagementAccess = (user: ReturnType<typeof useAuth>['user']): boolean => {
  if (!user) return false;
  if (user.is_superuser) return true;
  const appPermissions = user.app_permissions?.[APP_SLUG] ?? user.permissions?.[APP_SLUG];
  return appPermissions?.content_management === true;
};

const ContentManagementGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  return hasContentManagementAccess(user) ? <>{children}</> : <Navigate to="/" replace />;
};

const AnimatedRoutes: React.FC = () => {
  const location = useLocation();
  const { isLoading, isAuthenticated, login, user } = useAuth();

  // Set Sentry user context when authenticated
  React.useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id, email: user.email, username: user.full_name || user.email });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  React.useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      login();
    }
  }, [isLoading, isAuthenticated, login]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center flex-col gap-4">
        <img
          src="https://storage.orionex.id/orionex-storage/orionex.webp"
          alt="Orionex"
          className="h-12 object-contain animate-pulse"
        />
        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
          <RotateCw className="animate-spin" size={18} />
          <span className="text-sm font-medium">Authenticating...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // AuthProvider handles redirect
  }

  return (
    <AnimatePresence mode="wait">
      <React.Suspense fallback={
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
          <RotateCw className="animate-spin text-orionBlue" size={24} />
        </div>
      }>
        {/* @ts-ignore - React Router v6 types sometimes reject key even though it's standard for Framer Motion */}
        <Routes location={location} key={location.pathname}>
          <Route element={<MainLayout />}>
            <Route path="/" element={<PageTransition><Dashboard /></PageTransition>} />
            <Route path="/llm-logger" element={<PageTransition><LLMLogger /></PageTransition>} />
            <Route path="/mail-notifier" element={<PageTransition><MailNotifier /></PageTransition>} />
            <Route path="/invoices" element={<PageTransition><InvoicingService /></PageTransition>} />
            <Route path="/subscriptions" element={<PageTransition><SubscriptionsPage /></PageTransition>} />
            <Route path="/payment-apps" element={<PageTransition><PaymentApps /></PageTransition>} />
            <Route path="/webhook-dead-letter" element={<PageTransition><WebhookDeadLetter /></PageTransition>} />
            <Route path="/compliance" element={<PageTransition><CompliancePage /></PageTransition>} />
            <Route path="/apps" element={<PageTransition><AppsManagement /></PageTransition>} />
            <Route path="/users" element={<PageTransition><UserManagement /></PageTransition>} />
            <Route path="/service-agents" element={<PageTransition><ServiceAgentsManagement /></PageTransition>} />
            <Route path="/content/schemas" element={
              <ContentManagementGuard>
                <PageTransition><SchemasPage /></PageTransition>
              </ContentManagementGuard>
            } />
            <Route path="/content/schemas/new" element={
              <ContentManagementGuard>
                <PageTransition><SchemaCreatePage /></PageTransition>
              </ContentManagementGuard>
            } />
            <Route path="/content/schemas/:schemaId" element={
              <ContentManagementGuard>
                <PageTransition><SchemaDetailPage /></PageTransition>
              </ContentManagementGuard>
            } />
            <Route path="/content/contents" element={
              <ContentManagementGuard>
                <PageTransition><ContentsPage /></PageTransition>
              </ContentManagementGuard>
            } />
            <Route path="/content/contents/new" element={
              <ContentManagementGuard>
                <PageTransition><ContentCreatePage /></PageTransition>
              </ContentManagementGuard>
            } />
            <Route path="/content/contents/:contentId" element={
              <ContentManagementGuard>
                <PageTransition><ContentEditPage /></PageTransition>
              </ContentManagementGuard>
            } />
            <Route path="/legal/public" element={
              <ContentManagementGuard>
                <PageTransition><PublicLegalPage /></PageTransition>
              </ContentManagementGuard>
            } />
          </Route>
        </Routes>
      </React.Suspense>
    </AnimatePresence>
  );
};

const SentryFallback: React.FC<{ error: Error; resetError: () => void }> = ({ error, resetError }) => (
  <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
    <div className="max-w-md mx-auto text-center p-8">
      <div className="text-6xl mb-4">&#x26A0;</div>
      <h1 className="text-xl font-bold text-slate-800 dark:text-white mb-2">Something went wrong</h1>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{error.message}</p>
      <button onClick={resetError} className="px-6 py-2 bg-orionBlue text-white rounded-lg font-medium text-sm hover:opacity-90 transition-opacity">
        Try Again
      </button>
    </div>
  </div>
);

const AUTH_URL = window.location.hostname === 'admin.orionex.id'
  ? 'https://auth-dev.orionex.id'
  : 'https://auth-dev.orionex.id';

const App: React.FC = () => {
  return (
    <Sentry.ErrorBoundary fallback={({ error, resetError }) => <SentryFallback error={error} resetError={resetError} />}>
      <AuthProvider
        authUrl={AUTH_URL}
        clientId="rolling_skeleton"
        redirectUri={window.location.origin}
        mode="first-party"
      >
        <BrowserRouter>
          <AnimatedRoutes />
        </BrowserRouter>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  );
};

export default App;
